import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useState } from "react";
import { Loader2, Scale, CheckCircle2, AlertTriangle, Printer, Download, FileSpreadsheet } from "lucide-react";
import { printStatements, pdfStatement, excelStatements, ledgerToStatement } from "../lib/statementPrint";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { PageHeader, Card, useFin } from "../FinanceApp";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export function FinTrialBalance() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from_date: "", to_date: "" });
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from_date) params.from_date = range.from_date;
      if (range.to_date) params.to_date = range.to_date;
      setData(await finApi.trialBalance(params));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div data-testid="fin-trial-balance">
      <PageHeader title="ميزان المراجعة" subtitle="أرصدة الحسابات المدينة والدائنة" />
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="space-y-1"><Label className="text-xs">من تاريخ</Label><Input type="date" value={range.from_date} onChange={(e) => setRange({ ...range, from_date: e.target.value })} className="w-40" /></div>
        <div className="space-y-1"><Label className="text-xs">إلى تاريخ</Label><Input type="date" value={range.to_date} onChange={(e) => setRange({ ...range, to_date: e.target.value })} className="w-40" /></div>
        <Button onClick={load} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fin-tb-apply">عرض</Button>
      </div>
      {loading || !data ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
        <Card className="overflow-hidden">
          <div className={`px-4 py-3 flex items-center gap-2 text-sm font-semibold ${data.balanced ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`} data-testid="fin-tb-balance-status">
            {data.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {data.balanced ? "الميزان متوازن" : "الميزان غير متوازن"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right"><thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">الرمز</th><th className="px-3 py-2 font-semibold">الحساب</th><th className="px-3 py-2 font-semibold">مدين</th><th className="px-3 py-2 font-semibold">دائن</th></tr></thead>
              <tbody className="font-mono-nums">{data.rows.map((r) => (
                <tr key={r.account_id} className="border-t border-slate-50"><td className="px-3 py-2 text-slate-500">{r.code}</td><td className="px-3 py-2 font-sans">{r.name}</td><td className="px-3 py-2" dir="ltr">{r.debit ? <>{money(r.debit)} <SaudiRiyal /></> : "—"}</td><td className="px-3 py-2" dir="ltr">{r.credit ? <>{money(r.credit)} <SaudiRiyal /></> : "—"}</td></tr>
              ))}</tbody>
              <tfoot className="bg-slate-50 font-mono-nums font-bold"><tr className="border-t-2 border-slate-300"><td colSpan={2} className="px-3 py-2 font-sans">الإجمالي</td><td className="px-3 py-2" dir="ltr">{money(data.total_debit)} <SaudiRiyal /></td><td className="px-3 py-2" dir="ltr">{money(data.total_credit)} <SaudiRiyal /></td></tr></tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export function FinLedger() {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { user } = useFin();
  useEffect(() => { finApi.accounts(false).then((a) => setAccounts(a.filter((x) => x.allow_posting))).catch((e) => toast.error(apiErr(e))); }, []);
  useEffect(() => { if (!accId) return; setLoading(true); finApi.generalLedger({ account_id: accId, from_date: from || undefined, to_date: to || undefined }).then(setData).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false)); }, [accId, from, to]);
  const st = data && ledgerToStatement(data, from, to);
  return (
    <div data-testid="fin-ledger">
      <PageHeader title="دفتر الأستاذ العام" subtitle="حركة الحساب والرصيد المتحرك" />
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="w-full max-w-md"><Select value={accId} onValueChange={setAccId}>
          <SelectTrigger data-testid="fin-ledger-account"><SelectValue placeholder="اختر حساباً" /></SelectTrigger>
          <SelectContent className="max-h-72">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
        </Select></div>
        <label className="text-xs text-slate-500">من<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="fin-ledger-from" /></label>
        <label className="text-xs text-slate-500">إلى<Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="fin-ledger-to" /></label>
        {st && <>
          <Button size="sm" onClick={() => printStatements([st], user?.company)} className="gap-1.5" data-testid="fin-ledger-print"><Printer className="h-4 w-4" /> طباعة كشف الحساب</Button>
          <Button size="sm" variant="outline" onClick={() => pdfStatement(st, user?.company, `كشف-${data.account.code}.pdf`)} className="gap-1.5" data-testid="fin-ledger-pdf"><Download className="h-4 w-4" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => excelStatements([st], `كشف-${data.account.code}.xlsx`)} className="gap-1.5" data-testid="fin-ledger-excel"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
        </>}
      </div>
      {!accId ? <Card className="py-16 text-center text-slate-400">اختر حساباً لعرض حركته</Card>
        : loading || !data ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
          <Card className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 flex items-center justify-between text-sm">
              <span className="font-bold text-slate-800">{data.account.code} · {data.account.name}</span>
              <span className="font-mono-nums" dir="ltr">الرصيد الختامي: {money(data.closing_balance)} <SaudiRiyal /></span>
            </div>
            <div className="overflow-x-auto"><table className="w-full text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">التاريخ</th><th className="px-3 py-2 font-semibold">القيد</th><th className="px-3 py-2 font-semibold">البيان</th><th className="px-3 py-2 font-semibold">مدين</th><th className="px-3 py-2 font-semibold">دائن</th><th className="px-3 py-2 font-semibold">الرصيد</th></tr></thead>
              <tbody className="font-mono-nums">
                <tr className="border-t border-slate-100 bg-slate-50/40"><td colSpan={5} className="px-3 py-2 font-sans text-slate-500">رصيد افتتاحي</td><td className="px-3 py-2" dir="ltr">{money(data.opening_balance)} <SaudiRiyal /></td></tr>
                {data.transactions.map((t, i) => (
                  <tr key={i} className="border-t border-slate-50"><td className="px-3 py-2 text-slate-500">{t.date}</td><td className="px-3 py-2">{t.number}</td><td className="px-3 py-2 font-sans">{t.description || "—"}</td><td className="px-3 py-2" dir="ltr">{t.debit ? <>{money(t.debit)} <SaudiRiyal /></> : "—"}</td><td className="px-3 py-2" dir="ltr">{t.credit ? <>{money(t.credit)} <SaudiRiyal /></> : "—"}</td><td className="px-3 py-2 font-semibold" dir="ltr">{money(t.balance)} <SaudiRiyal /></td></tr>
                ))}
              </tbody>
            </table></div>
          </Card>
        )}
    </div>
  );
}

export function FinAudit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { finApi.auditLogs().then(setRows).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false)); }, []);
  const ACT = { create: "إنشاء", update: "تعديل", delete: "حذف", post: "ترحيل", reverse: "عكس", seed: "تهيئة", register: "تسجيل" };
  return (
    <div data-testid="fin-audit">
      <PageHeader title="سجل التدقيق" subtitle="سجل غير قابل للتعديل لكل العمليات" />
      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>
          : rows.length === 0 ? <div className="py-16 text-center text-slate-400">لا توجد سجلات</div> : (
            <div className="overflow-x-auto"><table className="w-full text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">التوقيت</th><th className="px-3 py-2 font-semibold">المستخدم</th><th className="px-3 py-2 font-semibold">الإجراء</th><th className="px-3 py-2 font-semibold">العنصر</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-50"><td className="px-3 py-2 text-slate-500 font-mono-nums" dir="ltr">{(r.at || "").slice(0, 19).replace("T", " ")}</td><td className="px-3 py-2">{r.user_name || r.user_email}</td><td className="px-3 py-2">{ACT[r.action] || r.action}</td><td className="px-3 py-2 text-slate-500">{r.entity}</td></tr>
              ))}</tbody>
            </table></div>
          )}
      </Card>
    </div>
  );
}
